import { captureException } from '@sentry/react'
import { Duration } from 'luxon'

import { AlertingMetricValueType, AlertingPolicyCreateRequest } from 'types'

import { AlertingPolicyForm } from './types'

export const SLACK_GROUP_PREFIX = 'GROUP-'
export const SLACK_USER_PREFIX = 'USER-'

export const addPrefix = (list: string[], prefix: string) =>
  list.map(item => `${prefix}${item}`)
export const removePrefix = (list: string[], prefix: string) =>
  list.map(item => item.replace(prefix, ''))

export const convertPolicyToForm = (
  policy: AlertingPolicyCreateRequest,
): AlertingPolicyForm => ({
  ...policy,
  thresholds: policy.thresholds.map(threshold => ({
    ...threshold,
    slackUsersAndGroups: [
      ...addPrefix(threshold.slackUsers, SLACK_USER_PREFIX),
      ...addPrefix(threshold.slackUserGroups, SLACK_GROUP_PREFIX),
    ],
    value: {
      ...threshold.value,
      value:
        threshold.value.type === AlertingMetricValueType.Duration
          ? durationFromISO(threshold.value.value).as('minutes').toString()
          : threshold.value.value,
    },
  })),
})

export const convertFormToPolicy = (
  policy: AlertingPolicyForm,
): AlertingPolicyCreateRequest => ({
  ...policy,
  slackChannel: policy.slackChannel.replace('#', ''),
  thresholds: policy.thresholds.map(({ slackUsersAndGroups, ...threshold }) => {
    let value = threshold.value.value

    try {
      value =
        threshold.value.type === AlertingMetricValueType.Duration
          ? durationToISO(durationFromMinutes(Number(threshold.value.value)))
          : threshold.value.value
    } catch (e) {
      captureException(e)
    }

    const slackUserGroups = slackUsersAndGroups.filter(userOrGroups =>
      userOrGroups.startsWith(SLACK_GROUP_PREFIX),
    )
    const slackUsers = slackUsersAndGroups.filter(userOrGroups =>
      userOrGroups.startsWith(SLACK_USER_PREFIX),
    )
    const slackUserGroupsWithoutPrefix = removePrefix(slackUserGroups, SLACK_GROUP_PREFIX)
    const slackUsersWithoutPrefix = removePrefix(slackUsers, SLACK_USER_PREFIX)

    return {
      ...threshold,
      slackUserGroups: slackUserGroupsWithoutPrefix,
      slackUsers: slackUsersWithoutPrefix,
      value: {
        ...threshold.value,
        value,
      },
    }
  }),
})

function durationFromISO(duration: string): Duration {
  const parsedDuration = Duration.fromISO(duration)

  if (!parsedDuration.isValid) {
    throw new RangeError(
      parsedDuration.invalidExplanation ?? `Invalid duration: ${duration}`,
    )
  }

  return parsedDuration
}

function durationFromMinutes(minutes: number): Duration {
  if (!Number.isFinite(minutes)) {
    throw new RangeError(`Invalid minutes: ${minutes}`)
  }

  return Duration.fromObject({ minutes })
}

function durationToISO(duration: Duration): string {
  const isoDuration = duration.toISO()

  if (!isoDuration) {
    throw new RangeError('Failed to convert duration to ISO string')
  }

  return isoDuration
}
